import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import IneligibleUsersNotice from "@/components/assignments/IneligibleUsersNotice";
import type { FormValues, FormSettings, IneligibleUsersResponse } from "@/api/assignments";

interface ValidationErrors {
  path?: { hasError?: boolean; message?: string };
  audience?: { hasError?: boolean; message?: string };
  settings?: {
    name?: { hasError?: boolean; message?: string };
    description?: { hasError?: boolean; message?: string };
    date?: { hasError?: boolean; message?: string };
  };
}

interface AssignmentReviewProps {
  values: FormValues;
  onStepChange: (step: number) => void;
  validationErrors?: ValidationErrors;
  isNameUnique?: boolean;
  isFormValid?: boolean;
  ineligibleUsers?: IneligibleUsersResponse;
}

const formatDate = (dateString: Date | string | null | undefined): string => {
  if (!dateString) return "Not specified";
  return new Date(dateString).toLocaleDateString();
};

const AssignmentReview = ({
  values = { selectedPath: null, target: { availableUsers: [], users: [] }, settings: {} },
  onStepChange,
  validationErrors,
  isNameUnique = true,
  isFormValid = true,
  ineligibleUsers,
}: AssignmentReviewProps) => {
  const selectedPath = values.selectedPath;
  const usersCount = values.target?.users?.length ?? 0;
  const settings: FormSettings = values.settings ?? {};

  const settingsHasError =
    validationErrors?.settings?.name?.hasError ||
    validationErrors?.settings?.description?.hasError ||
    validationErrors?.settings?.date?.hasError ||
    !isNameUnique;

  return (
    <>
      {ineligibleUsers && <IneligibleUsersNotice data={ineligibleUsers} className="mb-4" />}

      <div className="grid 2xl:grid-cols-3 gap-4">
        {/* Path */}
        <Card className={themeClasses.bgElevation2}>
          <div className="flex justify-between items-center">
            <div className="flex gap-1 items-center mb-2">
              <h4 className="font-semibold text-lg">1. Path</h4>
              {validationErrors?.path?.hasError && (
                <span className="ml-2">
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="path-validation-error"
                  />
                  <Tooltip target="#path-validation-error" position="right">
                    {validationErrors.path.message}
                  </Tooltip>
                </span>
              )}
            </div>
            <Button label="Edit" onClick={() => onStepChange(0)} link />
          </div>
          {selectedPath ? (
            <div>
              <div className="mb-3">
                <span className="font-semibold">Path Name: </span>
                {selectedPath.name}
              </div>
              {selectedPath.levelName && (
                <div className="mb-3">
                  <span className="font-semibold">Level: </span>
                  {selectedPath.levelName}
                </div>
              )}
              {selectedPath.lessonsCount !== undefined && (
                <div className="mb-3">
                  <span className="font-semibold">Total Lessons: </span>
                  {selectedPath.lessonsCount}
                </div>
              )}
              {selectedPath.totalDuration && (
                <div>
                  <span className="font-semibold">Estimated Duration: </span>
                  {formatMinutes(selectedPath.totalDuration)}
                </div>
              )}
            </div>
          ) : (
            <div>Please select a path for your assignment.</div>
          )}
        </Card>

        {/* Audience */}
        <Card className={themeClasses.bgElevation2}>
          <div className="flex justify-between items-center">
            <div className="flex gap-1 items-center mb-2">
              <h4 className="font-semibold text-lg">2. Audience</h4>
              {validationErrors?.audience?.hasError && (
                <span className="ml-2">
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="audience-validation-error"
                  />
                  <Tooltip target="#audience-validation-error" position="right">
                    {validationErrors.audience.message}
                  </Tooltip>
                </span>
              )}
            </div>
            <Button label="Edit" onClick={() => onStepChange(1)} link />
          </div>
          {usersCount > 0 ? (
            <div>
              <span className="font-semibold">
                Selected {usersCount === 1 ? "User" : "Users"}:{" "}
              </span>
              {usersCount} {usersCount === 1 ? "user" : "users"} selected
            </div>
          ) : (
            <div>No users selected, please edit this section of your Assignment.</div>
          )}
        </Card>

        {/* Settings */}
        <Card className={`${themeClasses.bgElevation2} min-h-92`}>
          <div className="flex justify-between items-center">
            <div className="flex gap-1 items-center mb-2">
              <h4 className="font-semibold text-lg">3. Assignment Settings</h4>
              {settingsHasError && (
                <span className="ml-2">
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="settings-validation-error"
                  />
                  <Tooltip target="#settings-validation-error" position="right">
                    <ul className="list-disc pl-4">
                      {validationErrors?.settings?.name?.hasError && (
                        <li>{validationErrors.settings.name.message}</li>
                      )}
                      {validationErrors?.settings?.description?.hasError && (
                        <li>{validationErrors.settings.description.message}</li>
                      )}
                      {validationErrors?.settings?.date?.hasError && (
                        <li>{validationErrors.settings.date.message}</li>
                      )}
                      {!isNameUnique && <li>Assignment name already exists</li>}
                    </ul>
                  </Tooltip>
                </span>
              )}
            </div>
            <Button label="Edit" onClick={() => onStepChange(2)} link />
          </div>
          {(settings.name ??
          settings.endDate ??
          (settings as { description?: string }).description) ? (
            <>
              {settings.name && (
                <div className="mb-3">
                  <span className="font-semibold">Assignment Name: </span>
                  {settings.name}
                </div>
              )}
              {settings.dates?.dynamic && settings.dates?.dueAfter?.days ? (
                <div className="mb-3">
                  <span className="font-semibold">Duration: </span>
                  {settings.dates.dueAfter.days} day
                  {settings.dates.dueAfter.days !== 1 ? "s" : ""}
                </div>
              ) : settings.endDate ? (
                <div className="mb-3">
                  <span className="font-semibold">End Date: </span>
                  {formatDate(settings.endDate)}
                </div>
              ) : null}
              {(settings as { description?: string }).description && (
                <div>
                  <span className="font-semibold">Description: </span>
                  <div>{(settings as { description?: string }).description}</div>
                </div>
              )}
            </>
          ) : (
            <div>No settings configured, please edit this section of your Assignment.</div>
          )}
        </Card>
      </div>

      {!isFormValid && (
        <div className="flex justify-end mt-4">
          <ValidationMessage
            severity="warn"
            text="Please fill in all required fields before submitting"
          />
        </div>
      )}
    </>
  );
};

export default AssignmentReview;
